import { useState, useEffect, useRef, useCallback } from "react";
import { axiosInstance } from "../../config";
import "./RumblePitchPage.css";

const SESSION_KEY = "pitch_rumble_session";
const BATCH_INTERVAL_MS = 4000;
const HEATMAP_SAMPLE_MS = 300;

// ─── Gate Screen ────────────────────────────────────────────────────────────
function GateScreen({ onAccess }) {
  const [email, setEmail]     = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Please enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.post("/pitch/access", { email });
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        sessionId: res.data.sessionId,
        leadId:    res.data.leadId,
        email,
      }));
      onAccess(res.data.sessionId);
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp-gate">
      <div className="rp-gate-bg" />
      <div className="rp-gate-card">
        <div className="rp-gate-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="15" stroke="#FF6A00" strokeWidth="2"/>
            <path d="M11 10 L11 22 L23 16 Z" fill="#FF6A00"/>
          </svg>
          <span className="rp-gate-logo-text">CLIPBAIT</span>
          <span className="rp-gate-logo-x">×</span>
          <span className="rp-gate-logo-rumble">RUMBLE</span>
        </div>

        <h1 className="rp-gate-title">Strategic Acquisition Pitch</h1>
        <p className="rp-gate-sub">
          This presentation is confidential and intended for authorised recipients only.
        </p>

        <form className="rp-gate-form" onSubmit={submit}>
          <div className="rp-gate-field">
            <label>Email Address</label>
            <input
              type="email"
              placeholder="you@rumble.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              disabled={loading}
            />
          </div>

          {error && <div className="rp-gate-error">{error}</div>}

          <button className="rp-gate-btn" type="submit" disabled={loading}>
            {loading ? (
              <span className="rp-gate-spinner" />
            ) : (
              <>
                View Presentation
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </>
            )}
          </button>
        </form>

        <p className="rp-gate-footer">
          By accessing this deck you agree to keep its contents strictly confidential.
        </p>
      </div>
    </div>
  );
}

// ─── Analytics tracker hook ──────────────────────────────────────────────────
function useTracker(sessionId, iframeRef) {
  const queue       = useRef([]);
  const slideRef    = useRef(0);
  const slideStart  = useRef(Date.now());
  const mouseRef    = useRef({ x: 0, y: 0 });
  const heatTimer   = useRef(null);
  const flushTimer  = useRef(null);

  const push = useCallback((event) => {
    queue.current.push({ ...event, timestamp: Date.now() });
  }, []);

  const flush = useCallback(async () => {
    if (!sessionId || queue.current.length === 0) return;
    const batch = queue.current.splice(0);
    try {
      await axiosInstance.post("/pitch/event", { sessionId, events: batch });
    } catch (_) { /* silent */ }
  }, [sessionId]);

  // Periodic flush
  useEffect(() => {
    if (!sessionId) return;
    push({ type: "session_start", slideIndex: 0 });
    flushTimer.current = setInterval(flush, BATCH_INTERVAL_MS);

    const onVisibility = () => {
      if (document.hidden) {
        const now = Date.now();
        push({ type: "slide_leave", slideIndex: slideRef.current, durationMs: now - slideStart.current });
        flush();
      } else {
        slideStart.current = Date.now();
        push({ type: "slide_view", slideIndex: slideRef.current });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    const onUnload = () => {
      push({ type: "slide_leave", slideIndex: slideRef.current, durationMs: Date.now() - slideStart.current });
      push({ type: "session_end" });
      // Synchronous beacon for reliability
      const payload = JSON.stringify({ sessionId, events: queue.current.splice(0) });
      navigator.sendBeacon?.("/api/pitch/event", new Blob([payload], { type: "application/json" }));
    };
    window.addEventListener("beforeunload", onUnload);

    return () => {
      clearInterval(flushTimer.current);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onUnload);
      flush();
    };
  }, [sessionId, flush, push]);

  // Inject tracking into same-origin iframe
  useEffect(() => {
    if (!sessionId || !iframeRef.current) return;

    const iframe = iframeRef.current;

    const injectTracking = () => {
      try {
        const win = iframe.contentWindow;
        const doc = iframe.contentDocument;
        if (!win || !doc) return;

        // ── Heatmap mouse sampling ──
        const onMouseMove = (e) => {
          const rect = (doc.documentElement || doc.body).getBoundingClientRect?.() || {};
          const w = win.innerWidth  || 1;
          const h = win.innerHeight || 1;
          mouseRef.current = {
            x: Math.round((e.clientX / w) * 100),
            y: Math.round((e.clientY / h) * 100),
          };
        };

        heatTimer.current = setInterval(() => {
          push({ type: "heatmap", slideIndex: slideRef.current, ...mouseRef.current });
        }, HEATMAP_SAMPLE_MS);

        // ── Click tracking ──
        const onMouseClick = (e) => {
          const w = win.innerWidth  || 1;
          const h = win.innerHeight || 1;
          push({
            type: "click",
            slideIndex: slideRef.current,
            x: Math.round((e.clientX / w) * 100),
            y: Math.round((e.clientY / h) * 100),
          });
        };

        // ── Keyboard: detect slide nav ──
        const onKeyDown = (e) => {
          if (["ArrowRight","ArrowLeft","ArrowDown","ArrowUp","Space","PageDown","PageUp"].includes(e.code)) {
            push({ type: "key_press", key: e.code, slideIndex: slideRef.current });
          }
        };

        // ── Slide change: detect hash or aria-current slide ──
        let lastHash = win.location.hash;
        const detectSlide = () => {
          // Strategy 1: hash change
          const h = win.location.hash;
          if (h !== lastHash) {
            lastHash = h;
            const idx = parseInt(h.replace(/\D/g, "")) || 0;
            if (idx !== slideRef.current) changeSlide(idx);
          }
          // Strategy 2: look for .present, [data-active], aria-hidden=false slide
          const active =
            doc.querySelector?.(".present:not(.stack), [aria-roledescription='slide']:not([aria-hidden='true']), .swiper-slide-active") ||
            doc.querySelector?.("[data-index]");
          if (active) {
            const idx = parseInt(active.dataset?.index ?? active.dataset?.slidesIndex ?? active.getAttribute("data-index")) || 0;
            if (!isNaN(idx) && idx !== slideRef.current) changeSlide(idx);
          }
        };

        const changeSlide = (newIdx) => {
          const now = Date.now();
          push({ type: "slide_leave", slideIndex: slideRef.current, durationMs: now - slideStart.current });
          push({ type: "slide_view",  slideIndex: newIdx });
          slideRef.current = newIdx;
          slideStart.current = now;
        };

        const slidePoller = setInterval(detectSlide, 800);

        // Hide confidential tag
        const confStyle = doc.createElement("style");
        confStyle.textContent = ".confidential{display:none!important}";
        doc.head?.appendChild(confStyle);

        win.addEventListener("mousemove", onMouseMove,  { passive: true });
        win.addEventListener("click",     onMouseClick, { passive: true });
        win.addEventListener("keydown",   onKeyDown,    { passive: true });

        // Cleanup stored on iframe element for easy removal
        iframe._trackingCleanup = () => {
          clearInterval(slidePoller);
          clearInterval(heatTimer.current);
          try {
            win.removeEventListener("mousemove", onMouseMove);
            win.removeEventListener("click",     onMouseClick);
            win.removeEventListener("keydown",   onKeyDown);
          } catch (_) {}
        };
      } catch (err) {
        // Cross-origin guard — silently ignore
      }
    };

    iframe.addEventListener("load", injectTracking);
    return () => {
      iframe.removeEventListener("load", injectTracking);
      iframe._trackingCleanup?.();
    };
  }, [sessionId, iframeRef, push]);
}

// ─── Pitch Viewer ────────────────────────────────────────────────────────────
function PitchViewer({ sessionId }) {
  const iframeRef = useRef(null);
  useTracker(sessionId, iframeRef);

  const sendKey = (dir) => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc) {
        const btn = doc.getElementById(dir === "next" ? "next" : "prev");
        if (btn) { btn.click(); return; }
      }
      // Fallback: keyboard event
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      const key = dir === "next" ? "ArrowRight" : "ArrowLeft";
      const opts = { key, code: key, bubbles: true, cancelable: true };
      win.dispatchEvent(new KeyboardEvent("keydown", opts));
    } catch (_) {}
  };

  return (
    <div className="rp-viewer">
      <iframe
        ref={iframeRef}
        className="rp-iframe"
        src="/pitch-deck.html"
        title="Clipbait × Rumble Strategic Pitch"
        allowFullScreen
      />
      <button
        className="rp-nav-btn rp-nav-prev"
        onClick={() => sendKey("prev")}
        aria-label="Previous slide"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6"/>
        </svg>
      </button>
      <button
        className="rp-nav-btn rp-nav-next"
        onClick={() => sendKey("next")}
        aria-label="Next slide"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function RumblePitchPage() {
  const [sessionId, setSessionId] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(SESSION_KEY));
      return stored?.sessionId || null;
    } catch { return null; }
  });

  if (sessionId) return <PitchViewer sessionId={sessionId} />;
  return <GateScreen onAccess={setSessionId} />;
}
